import { Fragment } from 'react'
import { cn } from '@/lib/utils'
import { ExpectedBreakdown } from './ExpectedBreakdown'
import {
  DRAWER_PHRASING,
  tenderDeltaLabel,
  tenderTone,
  toneTextClass,
  type ExpectedBreakdownSpec,
  type TenderPhrasing,
} from './tenderMath'

/** One payment type on the review step. */
export interface ReviewRow {
  key: string
  label: string
  /** Small second line under the label ("paid to you later", …). */
  sublabel?: string
  expected: number
  counted: number
  /** counted − expected. */
  delta: number
  phrasing?: TenderPhrasing
  breakdown: ExpectedBreakdownSpec
}

/**
 * The review step's single statement: one row per payment type with
 * Expected / You counted / Difference, and the arithmetic behind each
 * expected figure one tap away under its row. Replaces the old banner +
 * math panel + "Differences to explain" trio, which printed the same three
 * differences three times (F-FE-N139).
 */
export function ReviewTable({
  rows,
  threshold,
  formatCurrency,
}: {
  rows: ReviewRow[]
  threshold: number
  formatCurrency: (n: number) => string
}) {
  return (
    <>
      {/* Phones: one stacked block per tender so the difference is never
          scrolled out of view. */}
      <div className="divide-y sm:hidden">
        {rows.map((r) => {
          const tone = tenderTone(r.delta, threshold)
          return (
            <div key={r.key} className="space-y-1 py-3 tabular-nums">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-base font-semibold">{r.label}</span>
                <span className={cn('text-right text-base font-semibold', toneTextClass(tone))}>
                  {tone === 'ok'
                    ? 'Matches'
                    : tenderDeltaLabel(r.delta, formatCurrency, r.phrasing ?? DRAWER_PHRASING)}
                </span>
              </div>
              {r.sublabel && <div className="text-xs text-muted-foreground">{r.sublabel}</div>}
              <div className="flex justify-between gap-3 text-sm text-muted-foreground">
                <span>Expected {formatCurrency(r.expected)}</span>
                <span>Counted {formatCurrency(r.counted)}</span>
              </div>
              <ExpectedBreakdown
                summary={`How we got ${formatCurrency(r.expected)}`}
                spec={r.breakdown}
                formatCurrency={formatCurrency}
              />
            </div>
          )
        })}
      </div>

      <div className="hidden overflow-x-auto sm:block">
      <table className="w-full border-collapse text-base tabular-nums">
        <thead>
          <tr className="text-xs font-medium text-muted-foreground">
            <th scope="col" className="border-b pb-2 pr-2 text-left font-medium">
              Payment
            </th>
            <th scope="col" className="border-b px-2 pb-2 text-right font-medium">
              Expected
            </th>
            <th scope="col" className="border-b px-2 pb-2 text-right font-medium">
              You counted
            </th>
            <th scope="col" className="border-b pb-2 pl-2 text-right font-medium">
              Difference
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const tone = tenderTone(r.delta, threshold)
            return (
              <Fragment key={r.key}>
                <tr>
                  <td className="pr-2 pt-3 align-top font-semibold">
                    {r.label}
                    {r.sublabel && (
                      <span className="block text-xs font-normal text-muted-foreground">
                        {r.sublabel}
                      </span>
                    )}
                  </td>
                  <td className="px-2 pt-3 text-right align-top">{formatCurrency(r.expected)}</td>
                  <td className="px-2 pt-3 text-right align-top">{formatCurrency(r.counted)}</td>
                  <td
                    className={cn(
                      'whitespace-nowrap pl-2 pt-3 text-right align-top font-semibold',
                      toneTextClass(tone),
                    )}
                  >
                    {tone === 'ok'
                      ? 'Matches'
                      : tenderDeltaLabel(r.delta, formatCurrency, r.phrasing ?? DRAWER_PHRASING)}
                  </td>
                </tr>
                <tr>
                  <td colSpan={4} className="border-b pb-2">
                    <ExpectedBreakdown
                      summary={`How we got ${formatCurrency(r.expected)}`}
                      spec={r.breakdown}
                      formatCurrency={formatCurrency}
                    />
                  </td>
                </tr>
              </Fragment>
            )
          })}
        </tbody>
      </table>
      </div>
    </>
  )
}
